import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { casesAPI } from '../services/api';

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCase = async () => {
      try {
        const data = await casesAPI.getById(id);
        setCaseStudy({
          clientName: data.client_name,
          age: data.age,
          city: data.city,
          challenge: data.challenge,
          solution: data.solution,
          beforeImage: data.before_image_url,
          afterImage: data.after_image_url,
          results: data.results,
          testimonial: data.testimonial
        });
      } catch (e) {
        setCaseStudy(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [id]);

  if (loading) {
    return (
      <div className="container" style={{ padding: 'var(--space-xxxl) var(--space-l)', textAlign: 'center' }}>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="container" style={{
        padding: 'var(--space-xxxl) var(--space-l)',
        textAlign: 'center'
      }}>
        <h2>Кейс не найден</h2>
        <Link to="/cases">
          <button className="btn-primary" style={{ marginTop: 'var(--space-l)' }}>
            Все кейсы
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: `linear-gradient(135deg, rgba(212, 196, 176, 0.15) 0%, rgba(250, 249, 247, 1) 100%)`,
        padding: 'calc(var(--space-xxxl) + 60px) var(--space-l) var(--space-xxxl)'
      }}>
        <div className="container">
          <Link to="/cases" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-s)',
            color: 'var(--warm-gray)',
            textDecoration: 'none',
            marginBottom: 'var(--space-l)',
            fontSize: '0.9375rem',
            transition: 'color 300ms ease'
          }}
          className="back-link">
            <ArrowLeft size={18} />
            Все кейсы
          </Link>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{ marginBottom: 'var(--space-m)' }}>
              История {caseStudy.clientName}
            </h1>
            <p style={{
              fontSize: '1.125rem',
              color: 'var(--warm-gray)',
              marginBottom: 'var(--space-s)'
            }}>
              {caseStudy.age} лет, {caseStudy.city}
            </p>
          </div>
        </div>
      </section>

      {/* Before/After Comparison */}
      <section className="section">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--space-xxl)',
            marginBottom: 'var(--space-xxl)'
          }}>
            <div>
              <h3 style={{
                marginBottom: 'var(--space-l)',
                textAlign: 'center',
                color: 'var(--warm-gray)'
              }}>
                До
              </h3>
              <div style={{
                height: '500px',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-subtle)'
              }}>
                <img
                  src={caseStudy.beforeImage}
                  alt="До трансформации"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: 'grayscale(20%)'
                  }}
                />
              </div>
            </div>
            <div>
              <h3 style={{
                marginBottom: 'var(--space-l)',
                textAlign: 'center',
                color: 'var(--warm-sage)'
              }}>
                После
              </h3>
              <div style={{
                height: '500px',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                boxShadow: 'var(--shadow-medium)'
              }}>
                <img
                  src={caseStudy.afterImage}
                  alt="После трансформации"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="section section-bg-gray">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 'var(--space-xxl)'
          }}>
            <div className="card" style={{ padding: 'var(--space-xl)' }}>
              <h2 style={{ marginBottom: 'var(--space-l)', fontSize: '1.5rem' }}>
                Задача
              </h2>
              <p style={{
                color: 'var(--warm-gray)',
                fontSize: '1.0625rem',
                lineHeight: '1.7'
              }}>
                {caseStudy.challenge}
              </p>
            </div>
            <div className="card" style={{
              padding: 'var(--space-xl)',
              backgroundColor: 'var(--warm-sage)',
              color: 'var(--cream-white)'
            }}>
              <h2 style={{
                marginBottom: 'var(--space-l)',
                fontSize: '1.5rem',
                color: 'var(--cream-white)'
              }}>
                Решение
              </h2>
              <p style={{
                color: 'var(--cream-white)',
                fontSize: '1.0625rem',
                lineHeight: '1.7',
                opacity: 0.95
              }}>
                {caseStudy.solution}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="section">
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{
              marginBottom: 'var(--space-xxl)',
              textAlign: 'center'
            }}>
              Результат работы
            </h2>
            <div style={{
              display: 'grid',
              gap: 'var(--space-l)'
            }}>
              {caseStudy.results.map((result, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--space-l)',
                  padding: 'var(--space-l)',
                  backgroundColor: 'var(--soft-gray)',
                  borderRadius: 'var(--radius-sm)'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--warm-sage)',
                    color: 'var(--cream-white)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Check size={18} />
                  </div>
                  <p style={{
                    color: 'var(--charcoal)',
                    fontSize: '1.0625rem',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {result}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="section section-bg-gray">
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '4rem',
              color: 'var(--warm-sage)',
              lineHeight: 1,
              marginBottom: 'var(--space-l)',
              fontFamily: 'var(--font-primary)'
            }}>
              "
            </div>
            <p style={{
              fontFamily: 'var(--font-primary)',
              fontSize: '1.5rem',
              color: 'var(--charcoal)',
              lineHeight: '1.6',
              marginBottom: 'var(--space-l)',
              fontStyle: 'italic'
            }}>
              {caseStudy.testimonial}
            </p>
            <p style={{
              color: 'var(--warm-gray)',
              fontSize: '1.0625rem'
            }}>
              — {caseStudy.clientName}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: 'var(--space-l)' }}>
            Хотите похожий результат?
          </h2>
          <p style={{
            fontSize: '1.0625rem',
            color: 'var(--warm-gray)',
            maxWidth: '600px',
            margin: '0 auto var(--space-xl)',
            lineHeight: '1.6'
          }}>
            Каждая история уникальна. Давайте создадим вашу трансформацию с учетом именно вашей ситуации и запросов.
          </p>
          <button
            className="btn-primary"
            onClick={() => navigate({ pathname: '/', hash: '#contact' })}
          >
            Оставить заявку
          </button>
        </div>
      </section>

      <style>{`
        .back-link:hover {
          color: var(--warm-sage);
        }
      `}</style>
    </div>
  );
};

export default CaseDetail;
